import React, { MutableRefObject, ReactElement, useState } from 'react';
import { CardBody } from 'reactstrap';
import { useTranslation } from '../translations';
import { Fetcher, useFetcher } from '../utils';
import { ListResponse } from '../utils/APIClient';
import { PaginationBar } from './PaginationBar';

interface Props<T> {
  readonly refreshRef?: MutableRefObject<(() => void) | null>;
  readonly pageSize: number;
  readonly renderer: (item: T, refresh: () => void) => React.JSX.Element;
  readonly dataFetcher: (page: number) => Promise<ListResponse<T>>;
}

export const PaginatedDataView = <T extends object>({
  dataFetcher,
  refreshRef,
  pageSize,
  renderer,
}: Props<T>): ReactElement | null => {
  const { ts } = useTranslation();
  const [page, setPage] = useState(0);

  const fetcher = useFetcher(async () => await dataFetcher(page), [page]);

  if (refreshRef) {
    refreshRef.current = fetcher.refresh;
  }

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  const { items, totalCount } = fetcher.data;

  return (
    <>
      {items.length === 0 ? (
        <CardBody>{ts('attendee_search.no_results_found')}</CardBody>
      ) : (
        <PaginationBar
          onChange={setPage}
          page={page}
          totalPages={Math.ceil(totalCount / pageSize)}
        />
      )}
      {items.map((item) => renderer(item, fetcher.refresh))}
    </>
  );
};
